import { useI18n, useT, type Translate } from "../../../../shared/i18n/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Check, FileUp, Info, Undo2 } from "lucide-react";
import { useRef, useState } from "react";
import type { DragEvent } from "react";
import { useTRPC } from "../../../../shared/api/trpc";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "../../../../shared/lib/cn";
import { formatRelativeTime } from "../../../../shared/lib/format-relative-time";
import type { WorkObjectFieldDefinition } from "../../../../entities/work-object-field/model/use-work-object-fields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ImportTarget =
  | { kind: "external_id" }
  | { kind: "external_url" }
  | { fieldDefinitionId: string; kind: "field" }
  | { kind: "ignore" }
  | { kind: "status" }
  | { kind: "title" }
  | { kind: "type" };

type ColumnMapping = { column: string; target: ImportTarget };

type Preview = {
  columns: string[];
  mapping: ColumnMapping[];
  plan: {
    ignoredColumns: string[];
    problems: Array<{ kind: string; rowNumber?: number; value?: string }>;
    rows: unknown[];
  };
  sampleRows: Array<Record<string, string>>;
  statusMatches: Array<{ incoming: string; matchedId: string | null; matchedName: string | null }>;
  typeMatches: Array<{ incoming: string; matchedId: string | null; matchedName: string | null }>;
  willCreate: number;
  willRestore: number;
  willUpdate: number;
};

type Result = { createdCount: number; id: string; restoredCount: number; skippedCount: number; updatedCount: number };

// Import is a place people are nervous: it is someone else's data, and it is
// about to land in a board other people are using. The flow is built around
// that — the review step says exactly what will happen, nothing is dropped
// without being named, and the undo is offered the moment the import lands.
export function ImportItemsDialog({
  boardId,
  fields,
  onImported,
  onOpenChange,
  open,
}: {
  boardId: string;
  fields: WorkObjectFieldDefinition[];
  onImported?: () => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const previewMutation = useMutation(trpc.imports.preview.mutationOptions());
  const applyMutation = useMutation(trpc.imports.run.mutationOptions());
  const revertMutation = useMutation(trpc.imports.revert.mutationOptions());
  // Undo has to outlive this dialog. Someone closes the window, walks away,
  // then wants the import back out — offering it only in the moment right
  // after the import would make that a one-second window.
  const historyQuery = useQuery({ ...trpc.imports.history.queryOptions({ boardId }), enabled: open });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState("");
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [isReverted, setIsReverted] = useState(false);
  // Откат подтверждается отдельно: он убирает не всё, что импорт сделал, и
  // человек должен увидеть разницу до нажатия, а не узнать о ней после.
  const [pendingUndoBatchId, setPendingUndoBatchId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Closing from inside has to go through the same reset as closing from the
  // overlay, or reopening lands on the last import's finished screen instead of
  // somewhere a new file can be dropped.
  function close() {
    onOpenChange(false);
    reset();
  }

  function reset() {
    setFileName("");
    setContent("");
    setPreview(null);
    setResult(null);
    setIsReverted(false);
    setError(null);
  }

  async function loadFile(file: File) {
    setError(null);
    const text = await file.text();
    setFileName(file.name);
    setContent(text);
    try {
      setPreview((await previewMutation.mutateAsync({ boardId, content: text, fileName: file.name })) as Preview);
    } catch (previewError) {
      setError(previewError instanceof Error ? previewError.message : t("importItems.couldNotRead"));
    }
  }

  // Changing a mapping re-plans on the server, so the numbers on screen always
  // come from the same code that will do the import.
  async function changeMapping(column: string, target: ImportTarget) {
    if (!preview) return;
    const mapping = preview.mapping.map((entry) => (entry.column === column ? { column, target } : entry));
    setPreview({ ...preview, mapping });
    try {
      setPreview((await previewMutation.mutateAsync({ boardId, content, fileName, mapping })) as Preview);
    } catch (mappingError) {
      setError(mappingError instanceof Error ? mappingError.message : t("importItems.couldNotApplyMapping"));
    }
  }

  async function runImport() {
    if (!preview) return;
    setError(null);
    try {
      const applied = (await applyMutation.mutateAsync({ boardId, content, fileName, mapping: preview.mapping })) as Result;
      setResult(applied);
      await queryClient.invalidateQueries({ queryKey: trpc.boards.list.queryKey() });
      await onImported?.();
    } catch (applyError) {
      setError(applyError instanceof Error ? applyError.message : t("importItems.couldNotImport"));
    }
  }

  async function undoImport(batchId: string) {
    setError(null);
    setPendingUndoBatchId(null);
    try {
      await revertMutation.mutateAsync({ batchId });
      if (batchId === result?.id) setIsReverted(true);
      await queryClient.invalidateQueries({ queryKey: trpc.boards.list.queryKey() });
      await historyQuery.refetch();
      await onImported?.();
    } catch (revertError) {
      setError(revertError instanceof Error ? revertError.message : t("importItems.couldNotUndo"));
    }
  }

  const importableFields = fields.filter((field) => field.origin !== "system" && !isUnimportable(field.type));
  const missingTitle = preview?.plan.problems.filter((problem) => problem.kind === "missing-title").length ?? 0;
  const duplicateIds = preview?.plan.problems.filter((problem) => problem.kind === "duplicate-external-id").length ?? 0;
  const hasTitleColumn = !preview?.plan.problems.some((problem) => problem.kind === "no-title-column");
  const unmatchedStatuses = preview?.statusMatches.filter((match) => match.matchedId === null) ?? [];
  const batches = (historyQuery.data ?? []).slice(0, 3);
  const rowCount = preview ? preview.willCreate + preview.willUpdate + preview.willRestore : 0;

  return (
    <Dialog
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
      open={open}
    >
      <DialogContent className="flex max-h-[min(720px,calc(100dvh-2rem))] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {result ? t("importItems.finished") : preview ? t("importItems.reviewTitle") : t("importItems.title")}
          </DialogTitle>
          <DialogDescription>
            {result
              ? !canUndo(result)
                ? t("importItems.onlyRefreshed")
                : result.updatedCount > 0
                  ? t("importItems.undoPartial")
                  : t("importItems.undoAll")
              : preview
                ? t("importItems.reviewHint")
                : t("importItems.startHint")}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {result ? (
            <ImportSummary isReverted={isReverted} result={result} />
          ) : preview ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm">
                <span className="text-muted-foreground">{fileName}</span>
                {": "}
                {countPhrases(t, [
                  [preview.willCreate, "importItems.phraseNew"],
                  [preview.willUpdate, "importItems.phraseUpdate"],
                  [preview.willRestore, "importItems.phraseRestore"],
                ])}
              </p>

              <section className="flex flex-col gap-1.5">
                <h3 className="text-xs font-medium text-muted-foreground">{t("importItems.columns")}</h3>
                <div className="flex flex-col gap-1">
                  {preview.mapping.map((entry) => (
                    <div className="flex items-center gap-2" key={entry.column}>
                      <span className="min-w-0 flex-1 truncate text-sm" title={entry.column}>
                        {entry.column}
                        <span className="ml-2 text-xs text-muted-foreground/70">
                          {preview.sampleRows[0]?.[entry.column] || "—"}
                        </span>
                      </span>
                      <Select
                        onValueChange={(value) => {
                          void changeMapping(entry.column, targetFromValue(value ?? "ignore"));
                        }}
                        value={valueFromTarget(entry.target)}
                      >
                        <SelectTrigger
                          aria-label={t("a11y.importColumnTarget", { column: entry.column })}
                          className="h-7 w-52 shrink-0 text-xs"
                        >
                          <SelectValue>{targetLabel(entry.target, importableFields, t)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="title">{t("item.title")}</SelectItem>
                            <SelectItem value="status">{t("statuses.status")}</SelectItem>
                            <SelectItem value="type">{t("fieldType.type")}</SelectItem>
                            <SelectItem value="external_id">{t("importItems.originalId")}</SelectItem>
                            <SelectItem value="external_url">{t("importItems.linkToOriginal")}</SelectItem>
                            <SelectItem value="ignore">{t("importItems.skipColumn")}</SelectItem>
                          </SelectGroup>
                          {importableFields.length > 0 ? (
                            <SelectGroup>
                              <SelectLabel>{t("importItems.fieldsOnBoard")}</SelectLabel>
                              {importableFields.map((field) => (
                                <SelectItem key={field.id} value={`field:${field.id}`}>
                                  {field.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ) : null}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </section>

              {preview.willUpdate + preview.willRestore > 0 ? (
                <Note tone="info">
                  {preview.willUpdate > 0
                    ? t("importItems.updateHint")
                    : t("importItems.restoreHint")}
                </Note>
              ) : null}
              {unmatchedStatuses.length > 0 && preview.willCreate > 0 ? (
                <Note>
                  {unmatchedStatuses.map((match) => match.incoming).join(", ")}{" "}
                  {unmatchedStatuses.length === 1 ? "is not a status" : "are not statuses"} on this board — those items
                  will start where new work starts. You can move them afterwards.
                </Note>
              ) : null}
              {missingTitle > 0 ? (
                <Note>
                  {missingTitle} {missingTitle === 1 ? "row has" : "rows have"} no title and will be skipped.
                </Note>
              ) : null}
              {duplicateIds > 0 ? (
                <Note>
                  {duplicateIds} {duplicateIds === 1 ? "row repeats" : "rows repeat"} an ID that already appears earlier
                  in the file; only the first one is used.
                </Note>
              ) : null}
              {!hasTitleColumn ? <Note>{t("importItems.pickTitle")}</Note> : null}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
            <button
              className={cn(
                "flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center transition-colors",
                isDragging ? "border-primary/60 bg-primary/5" : "border-border/70 hover:bg-accent/30",
              )}
              onClick={() => fileInputRef.current?.click()}
              onDragLeave={() => setIsDragging(false)}
              onDragOver={(event: DragEvent) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDrop={(event: DragEvent) => {
                event.preventDefault();
                setIsDragging(false);
                const file = event.dataTransfer.files[0];
                if (file) void loadFile(file);
              }}
              type="button"
            >
              <FileUp className="size-6 text-muted-foreground" />
              <span className="text-sm font-medium">{t("importItems.drop")}</span>
              <span className="text-xs text-muted-foreground">
                {t("importItems.exportHint")}
              </span>
            </button>
            {batches.length > 0 ? (
              <section className="flex flex-col gap-1.5">
                <h3 className="text-xs font-medium text-muted-foreground">{t("importItems.earlier")}</h3>
                <div className="flex flex-col">
                  {batches.map((batch) => (
                    <div className="flex items-center gap-3 py-1 text-sm" key={batch.id}>
                      <span className="min-w-0 flex-1 truncate" title={batch.fileName}>
                        {batch.fileName || t("importItems.importedFile")}
                        <span className="ml-2 text-xs text-muted-foreground">
                          {countPhrases(t, [
                            [batch.createdCount, "importItems.phraseHistoryItems"],
                            [batch.updatedCount, "importItems.phraseHistoryUpdated"],
                            [batch.restoredCount, "importItems.phraseHistoryRestored"],
                          ])}{" "}
                          · {formatRelativeTime(batch.createdAt, t, locale)}
                        </span>
                      </span>
                      {batch.revertedAt ? (
                        <span className="shrink-0 text-xs text-muted-foreground">{t("importItems.undone")}</span>
                      ) : !canUndo(batch) ? null : (
                        <Button
                          className="h-7 shrink-0 text-xs"
                          disabled={revertMutation.isPending}
                          onClick={() => setPendingUndoBatchId(batch.id)}
                          size="sm"
                          variant="ghost"
                        >
                          <Undo2 data-icon="inline-start" />
                          {t("importItems.undo")}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
            </div>
          )}
          <input
            accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void loadFile(file);
              event.target.value = "";
            }}
            ref={fileInputRef}
            type="file"
          />
        </div>

        {error ? <p className="text-xs text-destructive">{error}</p> : null}

        <DialogFooter>
          {result ? (
            <>
              {!isReverted && canUndo(result) ? (
                <Button disabled={revertMutation.isPending} onClick={() => setPendingUndoBatchId(result.id)} variant="outline">
                  <Undo2 data-icon="inline-start" />
                  {t("importItems.undoImport")}
                </Button>
              ) : null}
              <Button onClick={close}>{t("common.done")}</Button>
            </>
          ) : preview ? (
            <>
              <Button onClick={reset} type="button" variant="ghost">
                <ArrowLeft data-icon="inline-start" />
                {t("importItems.chooseAnother")}
              </Button>
              <Button
                disabled={applyMutation.isPending || previewMutation.isPending || !hasTitleColumn || rowCount === 0}
                onClick={() => void runImport()}
              >
                <Check data-icon="inline-start" />
                {t("importItems.importN", { count: rowCount })}
              </Button>
            </>
          ) : (
            <Button onClick={close} type="button" variant="ghost">
              {t("common.cancel")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <UndoImportConfirmation
        batchId={pendingUndoBatchId}
        isPending={revertMutation.isPending}
        onCancel={() => setPendingUndoBatchId(null)}
        onConfirm={(batchId) => void undoImport(batchId)}
      />
    </Dialog>
  );
}

// Подтверждение отката, посчитанное на сервере.
//
// «Отменить импорт» звучит как «вернуть всё как было», но вернуть можно только
// созданное и поднятое из корзины: прежние значения полей у существовавших
// карточек нигде не сохранялись. Поэтому обе половины показываются числами до
// нажатия, а не описываются одной обтекаемой фразой после.
function UndoImportConfirmation({
  batchId,
  isPending,
  onCancel,
  onConfirm,
}: {
  batchId: string | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (batchId: string) => void;
}) {
  const t = useT();
  const trpc = useTRPC();
  const previewQuery = useQuery({
    ...trpc.imports.revertPreview.queryOptions({ batchId: batchId ?? "" }),
    enabled: batchId !== null,
  });
  const preview = previewQuery.data;

  return (
    <AlertDialog onOpenChange={(next) => (next ? undefined : onCancel())} open={batchId !== null}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("importItems.confirmUndo")}</AlertDialogTitle>
          <AlertDialogDescription>
            {previewQuery.isLoading || !preview ? (
              t("common.loading")
            ) : (
              <>
                {t("importItems.willArchive", { count: preview.willArchive.length })}
                {preview.willKeepChanges.length > 0 ? (
                  <span className="mt-2 block text-amber-600 dark:text-amber-400">
                    {t("importItems.willKeep", { count: preview.willKeepChanges.length })}
                  </span>
                ) : null}
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending || previewQuery.isLoading}
            onClick={() => {
              if (batchId) onConfirm(batchId);
            }}
          >
            {t("importItems.undoImport")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ImportSummary({ isReverted, result }: { isReverted: boolean; result: Result }) {
  const t = useT();
  const { locale } = useI18n();
  if (isReverted) {
    return (
      <p className="text-sm">
        {t("importItems.undoneMessage")}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <p>
        {countPhrases(t, [
          [result.createdCount, "importItems.phraseCreated"],
          [result.updatedCount, "importItems.phraseUpdated"],
          [result.restoredCount, "importItems.phraseRestored"],
        ])}
        .
      </p>
      {result.skippedCount > 0 ? (
        <p className="text-muted-foreground">
          {t("importItems.skippedRows", { count: result.skippedCount })}
        </p>
      ) : null}
    </div>
  );
}

// An import that only refreshed items already on the board has nothing to take
// back — offering an undo that cannot remove anything would report success and
// leave the board exactly as it is.
function canUndo(batch: { createdCount: number; restoredCount: number }): boolean {
  return batch.createdCount + batch.restoredCount > 0;
}

// "3 new items, 1 item brought back from the trash" — only what actually
// happens is named, so a plain import does not read like a list of caveats.
function countPhrases(t: Translate, entries: Array<[number, string]>): string {
  const parts = entries.filter(([count]) => count > 0).map(([count, key]) => t(key, { count }));
  return parts.length > 0 ? parts.join(", ") : t("importItems.nothingToImport");
}

// Two tones, because they are two different messages: something needs the
// reader's attention, or something is simply worth knowing. Reassurance dressed
// as a warning makes an ordinary import look like it is going wrong.
function Note({ children, tone = "warning" }: { children: React.ReactNode; tone?: "info" | "warning" }) {
  const t = useT();
  const { locale } = useI18n();
  const Icon = tone === "info" ? Info : AlertTriangle;
  return (
    <p
      className={cn(
        "flex items-start gap-2 rounded-md px-3 py-2 text-xs",
        tone === "info" ? "text-muted-foreground" : "bg-amber-500/10 text-amber-700 dark:text-amber-400",
      )}
    >
      <Icon className="mt-0.5 size-3.5 shrink-0" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

// The trigger has to be told what to print: the menu items only exist while the
// menu is open, so without this it falls back to the stored value and shows
// people a field id.
function targetLabel(target: ImportTarget, fields: WorkObjectFieldDefinition[], t: Translate): string {
  switch (target.kind) {
    case "external_id":
      return t("importItems.originalId");
    case "external_url":
      return t("importItems.linkToOriginal");
    case "field":
      return fields.find((field) => field.id === target.fieldDefinitionId)?.name ?? t("importItems.skipColumn");
    case "ignore":
      return t("importItems.skipColumn");
    case "status":
      return t("statuses.status");
    case "title":
      return t("item.title");
    case "type":
      return t("fieldType.type");
  }
}

function valueFromTarget(target: ImportTarget): string {
  return target.kind === "field" ? `field:${target.fieldDefinitionId}` : target.kind;
}

function targetFromValue(value: string): ImportTarget {
  if (value.startsWith("field:")) return { fieldDefinitionId: value.slice("field:".length), kind: "field" };
  return { kind: value as Exclude<ImportTarget, { kind: "field" }>["kind"] };
}

// Relations and computed values need identity resolution a flat file cannot
// give, so offering them would promise something that quietly fails.
function isUnimportable(type: WorkObjectFieldDefinition["type"]): boolean {
  return [
    "formula",
    "rollup",
    "type_reference",
    "user_relation",
    "user_multi_relation",
    "work_object_relation",
    "work_object_multi_relation",
  ].includes(type);
}
